/**
 * DeclineHandoffPage
 * Page for declining a handoff via SMS link: /handoff/decline/:token
 * Uses decline-link endpoint which validates and declines in one call
 */

import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from '@crisellevaldez/speculo-ui';
import { declineHandoffByToken } from '../../api/handoff';
import { FullPageLoader } from '../../components/common/FullPageLoader';

const DeclineHandoffPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const hasDeclined = useRef(false);
  const [isProcessing, setIsProcessing] = useState(true);

  // Automatically decline when page loads
  useEffect(() => {
    if (!token || hasDeclined.current) return;

    hasDeclined.current = true;

    const processDecline = async () => {
      try {
        // Call decline-link endpoint directly - validates and declines in one call
        const result = await declineHandoffByToken(token);

        if (result.success) {
          showToast({
            message: 'Handoff declined',
            variant: 'success',
          });
          navigate('/handoff/success?type=declined');
        } else {
          showToast({
            message: result.error || 'Error declining handoff',
            variant: 'error',
          });
          navigate(`/handoff/error?reason=${encodeURIComponent(result.error || 'Unknown error')}`);
        }
      } catch (error: any) {
        showToast({
          message: error.message || 'Error declining handoff',
          variant: 'error',
        });
        navigate(`/handoff/error?reason=${encodeURIComponent(error.message || 'Unknown error')}`);
      } finally {
        setIsProcessing(false);
      }
    };

    processDecline();
  }, [token, navigate, showToast]);

  // Handle missing token
  if (!token) {
    navigate('/handoff/error?reason=missing-token');
    return null;
  }

  // Show loading while processing decline
  return <FullPageLoader bgColor="bg-transparent" dotColor="bg-amber-500" />;
};

export default DeclineHandoffPage;
